/**
 * DOM-overlay HUD. Updated at ~10 Hz from the game loop — cheap string
 * writes, no per-frame layout thrash.
 */
import {WeaponChargeArc} from './weaponCharge';
import type { NetStatus } from "../net/net";

export interface HudState {
  systemName: string;
  planetName: string;
  /** Local biome under the player ("FOREST", "DUNE SEA", …); null hides the row. */
  biome: string | null;
  speed: number;
  altitude: number;
  mode: string;
  fps: number;
  net: NetStatus;
  players: number;
  /** Hyperlight cells aboard (each jump burns one). */
  cells: number;
  /** Present while on foot: the panel swaps to suit telemetry. */
  foot?: {
    hp: number;
    max: number;
    /** Personal shield (v50): 0/0 = none worn (row hidden). */
    shield: number;
    shieldMax: number;
    /** Active combat mode — ARM CANNON / SHIELD / LIGHT BLADE. */
    combat: string;
    /** ENERGY fraction 0..1: the one pool behind attacks, the shield and flight. */
    energy: number;
    flight?:{mode:string;speed:number;altitude:number};
    /** Environmental hazard line ("EXTREME HEAT", "VACUUM — SHIELDED 84S"). */
    hazard: string | null;
  };
}

export class Hud {
  readonly weaponCharge:WeaponChargeArc;
  private location: HTMLElement;
  private flight: HTMLElement;
  private net: HTMLElement;
  private toast: HTMLElement;
  private vehicleStatus: HTMLElement;
  private vehicleBoost: HTMLElement;
  private vehicleDrift: HTMLElement;
  private vehicleDriftLabel: HTMLElement;
  private vehicleBoostFill: HTMLElement;
  private vehicleDriftFill: HTMLElement;
  private vehicleBoostText: HTMLElement;
  private vehicleDriftText: HTMLElement;
  private toastTimer: number | null = null;

  constructor(root: HTMLElement) {
    root.innerHTML = `
      <div class="hud-panel" id="hud-location"></div>
      <div class="hud-panel" id="hud-flight"></div>
      <div class="hud-panel" id="hud-net"></div>
      <div id="hurt-vignette"></div>
      <div id="crosshair"></div>
      <div id="hitmarker"><i></i><i></i><i></i><i></i></div>
      <svg id="threat-ring" viewBox="-110 -110 220 220" width="220" height="220">
        ${Array.from({ length: 5 }, () =>
          `<path d="M -41.34 -66.14 A 78 78 0 0 1 41.34 -66.14" fill="none" stroke="#ff2d20"
                 stroke-width="8" stroke-linecap="round" style="display:none"></path>`).join("")}
      </svg>
      <div id="star-target"></div>
      <div id="lead-pip"></div>
      <svg id="charge-arc" width="100" height="38" viewBox="0 0 100 38">
        <g id="charge-ring-boost">
          <path d="M 8 12 A 60 60 0 0 0 92 12" fill="none" stroke="rgba(180,210,255,0.14)" stroke-width="6"></path>
          <path id="charge-arc-fill" d="M 8 12 A 60 60 0 0 0 92 12" fill="none" stroke="#5fd8ff" stroke-width="6"
                stroke-linecap="round" stroke-dasharray="93" stroke-dashoffset="93"></path>
        </g>
        <g id="charge-ring-jump">
          <path d="M 17.8 2 A 46 46 0 0 0 82.2 2" fill="none" stroke="rgba(255,200,140,0.13)" stroke-width="5"></path>
          <path id="charge-arc-fill2" d="M 17.8 2 A 46 46 0 0 0 82.2 2" fill="none" stroke="#ffb24a" stroke-width="5"
                stroke-linecap="round" stroke-dasharray="71" stroke-dashoffset="71"></path>
        </g>
      </svg>
      <div id="hud-toast"></div>
      <div id="hud-interact"></div>
      <div id="vehicle-status">
        <div class="vehicle-gauge vehicle-boost">
          <span>BOOST</span><div><i></i></div><b>0%</b>
        </div>
        <div class="vehicle-gauge vehicle-drift">
          <span>DRIFT</span><div><i></i></div><b>READY</b>
        </div>
      </div>
      <div id="hold-bar"><i></i></div>
      <div id="hud-system-banner"></div>
      <div id="hud-quest" style="position:fixed;top:190px;right:16px;max-width:262px;box-sizing:border-box;background:rgba(8,18,32,0.55);border:1px solid rgba(200,170,120,0.35);border-radius:4px;padding:8px 12px;backdrop-filter:blur(4px);font-family:monospace;display:none;z-index:9"></div>
    `;
    this.weaponCharge=new WeaponChargeArc(root);
    this.location = root.querySelector("#hud-location")!;
    this.flight = root.querySelector("#hud-flight")!;
    this.net = root.querySelector("#hud-net")!;
    this.toast = root.querySelector("#hud-toast")!;
    this.interact = root.querySelector("#hud-interact")!;
    this.quest = root.querySelector("#hud-quest")!;
    this.vehicleStatus = root.querySelector("#vehicle-status")!;
    this.vehicleBoost = this.vehicleStatus.querySelector(".vehicle-boost")!;
    this.vehicleDrift = this.vehicleStatus.querySelector(".vehicle-drift")!;
    this.vehicleDriftLabel = this.vehicleDrift.querySelector("span")!;
    this.vehicleBoostFill = this.vehicleBoost.querySelector("i")!;
    this.vehicleDriftFill = this.vehicleDrift.querySelector("i")!;
    this.vehicleBoostText = this.vehicleBoost.querySelector("b")!;
    this.vehicleDriftText = this.vehicleDrift.querySelector("b")!;
  }

  private quest: HTMLElement;
  private questLine: string | null = null;

  /** Tracked-quest objective readout (top-right). Null hides it. */
  setQuest(line: string | null): void {
    if (line === this.questLine) return;
    this.questLine = line;
    if (!line) {
      this.quest.style.display = "none";
      return;
    }
    this.quest.innerHTML =
      `<div style="color:#5d7e9e;font-size:10px;letter-spacing:2px">◆ TRACKED</div>` +
      `<div style="color:#ffe0b0;font-size:12px;line-height:1.4;margin-top:2px">${line}</div>`;
    this.quest.style.display = "block";
  }

  /** Hide the SYSTEM (top-left) and INTEGRITY/loadout (bottom-left) panels —
   *  the void editors (outfitter) strip the HUD to a featureless screen. */
  setPanelsVisible(visible: boolean): void {
    this.location.style.display = visible ? "" : "none";
    this.flight.style.display = visible ? "" : "none";
  }

  private interact: HTMLElement;
  private interactText: string | null = null;
  private crosshairVisible = true;
  private starTargetText: string | null = null;
  private pipVisible = false;

  /** Driving-only feedback. Boost is remaining energy; drift is live
   * handbrake engagement. Transform-based fills avoid per-frame layout. */
  setVehicleStatus(state: {
    boost: number;
    boosting: number;
    drift: number;
    gliding?: boolean;
    stalled?: boolean;
    airspeed?: number;
  } | null): void {
    this.vehicleStatus.classList.toggle("show", state !== null);
    if (!state) return;
    const boost = Math.max(0, Math.min(1, state.boost));
    const drift = Math.max(0, Math.min(1, state.drift));
    this.vehicleBoostFill.style.transform = `scaleX(${boost})`;
    const flightK = state.gliding ? Math.max(0, Math.min(1, (state.airspeed ?? 0) / 60)) : drift;
    this.vehicleDriftFill.style.transform = `scaleX(${flightK})`;
    this.vehicleBoostText.textContent = `${Math.round(boost * 100)}%`;
    this.vehicleDriftLabel.textContent = state.gliding ? "GLIDE" : "DRIFT";
    this.vehicleDriftText.textContent = state.gliding
      ? (state.stalled ? "STALL" : `${Math.round(state.airspeed ?? 0)} M/S`)
      : (drift > 0.08 ? `${Math.round(drift * 100)}%` : "READY");
    this.vehicleBoost.classList.toggle("active", state.boosting > 0.05);
    this.vehicleBoost.classList.toggle("low", boost < 0.2);
    this.vehicleDrift.classList.toggle("active", !!state.gliding || drift > 0.05);
  }

  /** Gun lead indicator: screen px, or null to hide. */
  setLeadPip(px: [number, number] | null): void {
    const el = document.getElementById("lead-pip");
    if (!el) return;
    if (!px) {
      if (this.pipVisible) {
        this.pipVisible = false;
        el.style.display = "none";
      }
      return;
    }
    if (!this.pipVisible) {
      this.pipVisible = true;
      el.style.display = "block";
    }
    el.style.transform = `translate(${px[0]}px, ${px[1]}px) translate(-50%, -50%)`;
  }

  private chargeShown = false;
  /** TWO concentric charge/countdown arcs under the player (screen px), so a boost-run and a charging
   *  jump can show AT ONCE. `boost` fills the OUTER cyan ring (boost-run ramp / engaged), `jump` fills
   *  the INNER amber ring (super-jump / big-leap charge); each is a 0..1 frac, or null to hide that ring.
   *  px null (or both null) hides the whole widget. */
  setChargeArc(px: [number, number] | null, boost: number | null, jump: number | null): void {
    const el = document.getElementById("charge-arc");
    const fill = document.getElementById("charge-arc-fill") as SVGElement | null;
    const fill2 = document.getElementById("charge-arc-fill2") as SVGElement | null;
    const ringBoost = document.getElementById("charge-ring-boost");
    const ringJump = document.getElementById("charge-ring-jump");
    if (!el || !fill || !fill2 || !ringBoost || !ringJump) return;
    if (!px || (boost === null && jump === null)) {
      if (this.chargeShown) {
        this.chargeShown = false;
        el.style.display = "none";
      }
      return;
    }
    if (!this.chargeShown) {
      this.chargeShown = true;
      el.style.display = "block";
    }
    el.style.transform = `translate(${px[0]}px, ${px[1]}px) translate(-50%, -50%)`;
    const C = 93; // OUTER arc length (R=60, chord 84)
    const C2 = 71; // INNER arc length (R=46) — the nested jump-charge ring
    ringBoost.style.display = boost === null ? "none" : "block";
    ringJump.style.display = jump === null ? "none" : "block";
    if (boost !== null) fill.style.strokeDashoffset = `${C * (1 - Math.max(0, Math.min(1, boost)))}`;
    if (jump !== null) fill2.style.strokeDashoffset = `${C2 * (1 - Math.max(0, Math.min(1, jump)))}`;
  }

  /** Readout under the crosshair when the nose rests on a neighbor star. */
  setStarTarget(text: string | null): void {
    if (text === this.starTargetText) return;
    this.starTargetText = text;
    const el = document.getElementById("star-target");
    if (!el) return;
    el.textContent = text ?? "";
    el.style.display = text ? "block" : "none";
  }
  private reticlePx = 8;

  /** Crosshair: always-on in flight, aim-only on foot. */
  setCrosshair(visible: boolean): void {
    if (visible === this.crosshairVisible) return;
    this.crosshairVisible = visible;
    const el = document.getElementById("crosshair");
    if (el) el.style.display = visible ? "" : "none";
  }

  /** Halo-style hit confirm: the four diagonal ticks around the reticle pop outward
   *  and fade (~0.2s). Driven by WAAPI rather than a CSS class toggle so a rapid
   *  string of confirms restarts the flash cleanly mid-fade — no reflow tricks. */
  hitMarker(): void {
    const el = document.getElementById("hitmarker");
    if (!el) return;
    el.animate(
      [
        { opacity: 0.95, transform: "scale(0.7)" },
        { opacity: 0.85, transform: "scale(1.08)", offset: 0.4 },
        { opacity: 0, transform: "scale(1.16)" },
      ],
      { duration: 220, easing: "ease-out" },
    );
  }

  /** Reticle bloom: ring diameter in px tracks the live weapon spread. */
  setReticle(px: number): void {
    const v = Math.round(Math.max(8, Math.min(90, px)));
    if (v === this.reticlePx) return;
    this.reticlePx = v;
    const el = document.getElementById("crosshair");
    if (el) {
      el.style.width = `${v}px`;
      el.style.height = `${v}px`;
    }
  }

  private holdFrac: number | null = null;

  /**
   * Hold-to-act progress: one shared bar under the interact prompt. Every
   * timed hold (F-board, F-disembark, the hyperlight charge) feeds it a
   * 0..1 fraction; null hides it. Single writer per frame — main owns it.
   */
  setHold(frac: number | null): void {
    if (frac === this.holdFrac) return;
    const el = document.getElementById("hold-bar");
    if (!el) return;
    const show = frac !== null;
    if (show !== (this.holdFrac !== null)) el.classList.toggle("show", show);
    this.holdFrac = frac;
    if (frac !== null) {
      (el.firstElementChild as HTMLElement).style.width = `${Math.min(1, frac) * 100}%`;
    }
  }

  /** Contextual interaction prompt (e.g. "F — join jump"); null hides it. */
  setInteract(text: string | null): void {
    if (text === this.interactText) return;
    this.interactText = text;
    this.interact.textContent = text ?? "";
    this.interact.classList.toggle("show", text !== null);
  }

  update(s: HudState): void {
    this.location.innerHTML =
      `<div class="label">SYSTEM</div><div class="value">${s.systemName}</div>` +
      `<div class="label">NEAREST BODY</div><div class="value">${s.planetName}</div>` +
      (s.biome ? `<div class="label">BIOME</div><div class="value">${s.biome}</div>` : "");

    if (s.foot) {
      // On foot the flight panel becomes suit telemetry.
      const hpClass = s.foot.hp <= s.foot.max * 0.3 ? "bad" : "ok";
      const energyPct = Math.round(s.foot.energy * 100);
      const energyClass = energyPct <= 25 ? "bad" : "ok";
      const shPct = s.foot.shieldMax > 0 ? Math.round((s.foot.shield / s.foot.shieldMax) * 100) : 0;
      this.flight.innerHTML =
        `<div class="label">INTEGRITY</div>` +
        `<div class="value ${hpClass}">${Math.round(s.foot.hp)} / ${s.foot.max}</div>` +
        (s.foot.shieldMax > 0
          ? `<div class="label">SHIELD</div>` +
            `<div class="value ${shPct <= 25 ? "warn" : "ok"}"><span class="energy-bar shield-bar"><i style="width:${shPct}%"></i></span> ${Math.round(s.foot.shield)} / ${s.foot.shieldMax}</div>`
          : "") +
        `<div class="label">COMBAT</div><div class="value">${s.foot.combat.toUpperCase()}</div>` +
        `<div class="label">ENERGY</div>` +
        `<div class="value ${energyClass}"><span class="energy-bar"><i style="width:${energyPct}%"></i></span> ${energyPct}%</div>` +
        (s.foot.flight?`<div class="label">${s.foot.flight.mode}</div><div class="value">${s.foot.flight.speed.toFixed(0)} m/s · ${Math.max(0,s.foot.flight.altitude).toFixed(0)} m</div><div class="label">SHIFT BOOST · X DIVE</div><div class="label">LAND TO RECHARGE</div>`:"") +
        (s.foot.hazard
          ? `<div class="label">HAZARD</div><div class="value ${s.foot.hazard.includes("SHIELDED") ? "warn" : "bad"}">${s.foot.hazard}</div>`
          : "") +
        `<div class="label">MODE</div><div class="value">${s.mode.toUpperCase()}</div>`;
    } else {
      const alt =
        s.altitude === Infinity
          ? "—"
          : s.altitude > 10000
            ? `${(s.altitude / 1000).toFixed(1)} km`
            : `${Math.max(0, s.altitude).toFixed(0)} m`;
      const speed =
        Math.abs(s.speed) > 10000
          ? `${(s.speed / 1000).toFixed(1)} km/s`
          : `${s.speed.toFixed(0)} m/s`;
      const cellClass = s.cells === 0 ? "bad" : s.cells === 1 ? "warn" : "";
      this.flight.innerHTML =
        `<div class="label">SPEED</div><div class="value">${speed}</div>` +
        `<div class="label">ALTITUDE</div><div class="value">${alt}</div>` +
        `<div class="label">CELLS</div><div class="value ${cellClass}">${s.cells}</div>` +
        `<div class="label">MODE</div><div class="value">${s.mode.toUpperCase()}</div>`;
    }

    const netClass = s.net === "online" ? "ok" : "bad";
    this.net.innerHTML =
      `<div class="label">NETWORK</div><div class="value ${netClass}">${s.net.toUpperCase()}</div>` +
      `<div class="label">PILOTS</div><div class="value">${s.net === "online" ? s.players : "—"}</div>` +
      `<div class="label">FPS</div><div class="value">${s.fps.toFixed(0)}</div>`;
  }

  private banner: HTMLElement | null = null;
  private bannerTimer: number | null = null;

  /**
   * System-name banner: fades in at the top-center, holds, fades out —
   * shown for ~2 s after warping in or logging in.
   */
  showSystemBanner(name: string): void {
    this.banner ??= document.getElementById("hud-system-banner");
    if (!this.banner) return;
    this.banner.textContent = name.toUpperCase();
    this.banner.classList.add("show");
    if (this.bannerTimer !== null) clearTimeout(this.bannerTimer);
    this.bannerTimer = window.setTimeout(() => this.banner?.classList.remove("show"), 2000);
  }

  private hurtEl: HTMLElement | null = null;
  private hurtTimer: number | null = null;

  /** RED HURT PULSE (2026-08-02, "damage to the player is hard to see"): a radial red
   *  vignette that flashes with each hit and fades — intensity scales with the damage. */
  hurtPulse(k: number): void {
    this.hurtEl ??= document.getElementById("hurt-vignette");
    const el = this.hurtEl;
    if (!el) return;
    el.style.transition = "none";
    el.style.opacity = String(Math.min(0.75, 0.3 + k * 0.5));
    if (this.hurtTimer !== null) window.clearTimeout(this.hurtTimer);
    this.hurtTimer = window.setTimeout(() => {
      el.style.transition = "opacity 0.55s ease-out";
      el.style.opacity = "0";
    }, 60);
  }

  private threatPaths: SVGPathElement[] | null = null;

  /**
   * THREAT RING (2026-08-17, user ask — CoD/Halo style): red arcs on a circle
   * around the reticle, each pointing at the BEARING incoming fire came from.
   * `arcs` is this frame's full list — deg is the screen bearing (0 = ahead/up,
   * clockwise positive), k the remaining-life fade 0..1. Caller (main) owns the
   * tracking pool and re-computes bearings per frame so arcs rotate with the
   * camera; this only paints. Empty list hides everything.
   */
  setThreatArcs(arcs: { deg: number; k: number }[]): void {
    this.threatPaths ??= Array.from(document.querySelectorAll<SVGPathElement>("#threat-ring path"));
    for (let i = 0; i < this.threatPaths.length; i++) {
      const el = this.threatPaths[i];
      const a = arcs[i];
      if (!a) {
        if (el.style.display !== "none") el.style.display = "none";
        continue;
      }
      el.style.display = "";
      el.setAttribute("transform", `rotate(${a.deg.toFixed(1)})`);
      el.style.opacity = (0.9 * Math.min(1, a.k)).toFixed(2);
    }
  }

  showToast(text: string, ms = 2600): void {
    this.toast.textContent = text;
    this.toast.classList.add("show");
    if (this.toastTimer !== null) clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => this.toast.classList.remove("show"), ms);
  }
}
